import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CATEGORIES, FontAwesomeIcon } from '@design-system/font-awesome-icon';
import { Link } from '@design-system/link';
import { useMatchBreakpoint } from '@design-system/theme';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';
import { baseLinkStyles, ItemComponent } from './ItemComponent';
import SubMenu from './SubMenu';
import { ITEM_FONTS, ITEM_SIZES } from '../constants';
const DEBOUNCE_DELAY = 100;
export const MenuItem = ({ menuItem, menuStyle, scrollContainerRef, }) => {
    var _a;
    const { forceDefaultLink, key, label, path, subItems, target } = menuItem;
    const isMenuItemActive = typeof window === 'undefined'
        ? false
        : ((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.pathname) === path ||
            Boolean((subItems === null || subItems === void 0 ? void 0 : subItems.length) &&
                subItems.some((subItem) => { var _a; return subItem.path === ((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.pathname); })) ||
            false;
    const isBreakpointMobile = useMatchBreakpoint('s');
    const fontSize = isBreakpointMobile ? ITEM_SIZES.small : ITEM_SIZES.normal;
    const { fontName, variantName } = ITEM_FONTS[fontSize];
    const menuItemRef = useRef(null);
    const subMenuRef = useRef(null);
    const [showSubMenu, setShowSubMenu] = useState(false);
    const [subMenuPosition, setSubMenuPosition] = useState(undefined);
    const updateSubMenuPosition = useCallback(() => {
        if (!menuItemRef.current || !scrollContainerRef.current) {
            return undefined;
        }
        const menuItemRect = menuItemRef.current.getBoundingClientRect();
        const menuItemComputedStyle = window.getComputedStyle(menuItemRef.current);
        const scrollContainerRect = scrollContainerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const menuItemLeftPadding = Number.parseInt(menuItemComputedStyle.getPropertyValue('padding-left'), 10);
        const position = menuItemRect.right + menuItemLeftPadding > scrollContainerRect.right
            ? {
                left: undefined,
                right: viewportWidth - scrollContainerRect.right,
            }
            : { left: menuItemRect.left + menuItemLeftPadding, right: undefined };
        setSubMenuPosition(Object.assign(Object.assign({}, position), { top: scrollContainerRect.bottom }));
        return undefined;
    }, [menuItemRef, scrollContainerRef]);
    const handleItemKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowDown':
                if (menuItem.subItems && subMenuRef.current) {
                    event.preventDefault();
                    updateSubMenuPosition();
                    const firstMenuItem = subMenuRef.current.querySelector('a');
                    firstMenuItem === null || firstMenuItem === void 0 ? void 0 : firstMenuItem.focus();
                }
                break;
            case 'Tab':
                setShowSubMenu(false);
                break;
            case 'Escape':
                if (showSubMenu) {
                    setShowSubMenu(false);
                }
                break;
        }
    };
    const handleMouseEventsOnMenuItem = (value) => {
        if (subItems) {
            if (value) {
                updateSubMenuPosition();
                setShowSubMenu(true);
            }
            else {
                setShowSubMenu(false);
            }
        }
    };
    const debouncedUpdatePosition = useCallback(() => debounce(updateSubMenuPosition, DEBOUNCE_DELAY)(), [updateSubMenuPosition]);
    const handleBlur = (event) => {
        var _a, _b;
        if (!((_a = menuItemRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget)) &&
            !((_b = subMenuRef.current) === null || _b === void 0 ? void 0 : _b.contains(event.relatedTarget))) {
            setShowSubMenu(false);
        }
    };
    const handleOnFocusOnClick = () => {
        if (menuItem.subItems) {
            updateSubMenuPosition();
            setShowSubMenu(true);
        }
    };
    useEffect(() => {
        if (isBreakpointMobile) {
            const handleScrollMobile = () => {
                setShowSubMenu(false);
            };
            window.addEventListener('scroll', handleScrollMobile, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScrollMobile);
            };
        }
        window.addEventListener('scroll', debouncedUpdatePosition);
        window.addEventListener('resize', debouncedUpdatePosition);
        return () => {
            window.removeEventListener('scroll', debouncedUpdatePosition);
            window.removeEventListener('resize', debouncedUpdatePosition);
        };
    }, [debouncedUpdatePosition, isBreakpointMobile, showSubMenu]);
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', () => setShowSubMenu(false));
        }
    }, [scrollContainerRef]);
    const a11yAttributes = {
        'aria-controls': subItems ? `subMenu-${key}` : undefined,
        'aria-current': isMenuItemActive ? 'page' : undefined,
        'aria-expanded': subItems ? showSubMenu : undefined,
        'aria-haspopup': subItems ? Boolean(subItems) : undefined,
        role: 'menuitem',
    };
    return (_jsxs(ItemComponent, { "$active": isMenuItemActive, "$isMenuItem": true, as: "li", font: isMenuItemActive ? variantName : fontName, onBlur: handleBlur, onClick: handleOnFocusOnClick, onFocus: handleOnFocusOnClick, onKeyDown: handleItemKeyDown, onMouseEnter: () => handleMouseEventsOnMenuItem(true), onMouseLeave: () => handleMouseEventsOnMenuItem(false), ref: menuItemRef, role: "none", children: [isBreakpointMobile && subItems ? (_jsx(SpanStyled, Object.assign({ "$active": isMenuItemActive, tabIndex: 0 }, a11yAttributes, { children: label }))) : (_jsx(LinkStyled, Object.assign({ "$active": isMenuItemActive, forceDefaultLink: forceDefaultLink, styledOnlyOnHover: true, target: target, to: path }, a11yAttributes, { children: label }))), subItems ? (_jsx(IconWrapper, { "$showSubMenu": showSubMenu, children: _jsx(FontAwesomeIcon, { height: "12px", iconName: "angle-down", iconType: CATEGORIES.SOLID }) })) : null, subItems &&
                createPortal(_jsx(SubMenu, { handleShowSubMenu: (value) => setShowSubMenu(value), isMobile: isBreakpointMobile, menuItem: menuItem, menuItemRef: menuItemRef, menuStyle: menuStyle, showSubMenu: showSubMenu, subMenuPosition: subMenuPosition, subMenuRef: subMenuRef }), document.body)] }));
};
const SpanStyled = styled.span `
  ${baseLinkStyles}
`;
const LinkStyled = styled(Link) `
  ${baseLinkStyles}

  &:hover {
    ${({ $active }) => !$active && `font-weight: normal !important;`}
  }
`;
const IconWrapper = styled.span `
  margin-left: 8px;
  display: inline-flex;
  transition: transform 0.5s ease;
  transform: rotate(
    ${({ $showSubMenu }) => ($showSubMenu ? '180deg' : '0deg')}
  );
`;
//# sourceMappingURL=MenuItem.js.map